import React, { useContext, useState } from 'react';
import { Card, CardContent, Typography, Button, Box, Alert, Tabs, Tab,
  LinearProgress, List, ListItem, ListItemIcon, ListItemText, 
  Checkbox, ListItemButton, Paper, CardHeader, useMediaQuery, Tooltip } from '@mui/material';
import { UserContext, UserData } from '../UserAndProfile/UserContext';
import { EditCalendar, CheckCircle, PlayArrow, Flag } from '@mui/icons-material';
import { format, parseISO, isBefore, addDays, eachDayOfInterval, formatISO } from 'date-fns';
import { getCurrentWeek, getTaskRoute, isStudyPlanStale, getWeeklyPaceMessage, getTodaysMission } from './studyPlanUtils';
import WeeklyProgressChart from './WeeklyProgressChart';
import { useTheme, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

interface StudyPlanWidgetProps {
  user: UserData | null;
  onCreatePlanClick: () => void;
  onViewPlanClick: () => void;
  embedded?: boolean;
}

const celebrateKeyframes = keyframes`
  0% { transform: scale(1); box-shadow: none; }
  50% { transform: scale(1.02); box-shadow: 0 0 15px rgba(76, 175, 80, 0.7); background-color: rgba(76, 175, 80, 0.1); }
  100% { transform: scale(1); box-shadow: none; background-color: transparent; }
`;

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const StudyPlanWidget: React.FC<StudyPlanWidgetProps> = ({ user, onCreatePlanClick, onViewPlanClick, embedded = false }) => {
  const { updateStudyPlanTask } = useContext(UserContext);
  const navigate = useNavigate();
  const studyPlanExists = !!user?.studyPlan;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [celebratingTaskId, setCelebratingTaskId] = useState<string | null>(null);
  const [widgetTab, setWidgetTab] = useState<'thisWeek' | 'progress'>('thisWeek');

  const handleCompleteTask = async (taskId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!user?.studyPlan) return;
    try {
      setCelebratingTaskId(taskId);
      await updateStudyPlanTask(taskId, {
        isCompleted: true,
        completionDate: new Date().toISOString()
      });
      setTimeout(() => setCelebratingTaskId(null), 1500);
    } catch (error) {
      console.error("Error completing task:", error);
      setCelebratingTaskId(null);
    }
  };

  const handleTaskClick = (taskId: string) => {
    if (!user?.studyPlan) return;
    const task = user.studyPlan.weeklyPlans.flatMap(w => w.tasks).find(t => t.id === taskId);
    if (!task) return;
    const route = getTaskRoute(task);
    if (route) navigate(route);
  };

  const renderStalePlan = () => (
    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, px: { xs: 2, sm: 3 } }}>
      <Typography variant={isMobile ? "h6" : "h5"} align="center" gutterBottom>Your test date has passed</Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
        Update your plan with a new test date to keep your study schedule on track.
      </Typography>
      <Button variant="contained" onClick={onCreatePlanClick} startIcon={<EditCalendar />} size="small">
        Update Your Plan
      </Button>
    </CardContent>
  );

  const renderPlanSummary = () => {
    if (!user || !user.studyPlan) return null;
    if (isStudyPlanStale(user.studyPlan)) return renderStalePlan();

    const { weeklyPlans } = user.studyPlan;
    const totalWeeks = weeklyPlans.length;
    const currentWeek = getCurrentWeek(user.studyPlan, new Date());
    if (!currentWeek) return null;

    const currentWeekNumber = currentWeek.weekNumber;
    const skillsGoal = currentWeek.weeklySkillsPracticeQuestionGoal;
    const missedGoal = currentWeek.weeklyReviewMissedQuestionGoal;
    const skillsProgress = skillsGoal.target > 0 ? (skillsGoal.current / skillsGoal.target) * 100 : 0;
    const missedProgress = missedGoal.target > 0 ? (missedGoal.current / missedGoal.target) * 100 : 0;
    const questionsThisWeek = currentWeek.questionsAttempted ?? 0;
    const activeDates = new Set(currentWeek.activeDates || []);

    const weekDays = eachDayOfInterval({
      start: parseISO(currentWeek.startDate),
      end: parseISO(currentWeek.endDate),
    });

    const pendingTestTask = currentWeek.tasks.find(
      t => t.type === 'full-practice-test' && !t.isCompleted
    );

    const upcomingTasks = weeklyPlans
      .flatMap(week => week.tasks)
      .filter(task =>
        !task.isCompleted &&
        !task.completionDate &&
        task.dueDate &&
        isBefore(parseISO(task.dueDate), addDays(new Date(), 7))
      )
      .sort((a, b) => parseISO(a.dueDate!).getTime() - parseISO(b.dueDate!).getTime());

    const completedTasks = currentWeek.tasks
      .filter(task => task.isCompleted || task.completionDate)
      .sort((a, b) =>
        (b.completionDate ? parseISO(b.completionDate).getTime() : 0) -
        (a.completionDate ? parseISO(a.completionDate).getTime() : 0)
      );

    const paceMessage = getWeeklyPaceMessage(currentWeek);
    const mission = getTodaysMission(user);

    const renderThisWeekTab = () => (
      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 0 }}>
        {mission && (
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderColor: 'primary.main', borderWidth: 1 }}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Flag color="primary" sx={{ mt: 0.25, fontSize: { xs: 18, sm: 22 } }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Today's Mission</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{mission.title}</Typography>
                {mission.subtitle && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{mission.subtitle}</Typography>
                )}
                <Button variant="contained" size="small" onClick={() => navigate(mission.route)} fullWidth={isMobile}>
                  Start
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {user.currentStreak && user.currentStreak > 0 && (
          <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
            {user.currentStreak}-week study streak
          </Typography>
        )}

        {pendingTestTask && (
            <Alert
              severity="info"
              icon={<PlayArrow />}
              action={
                <Button color="inherit" size="small" onClick={() => handleTaskClick(pendingTestTask.id)}
                  sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}>
                  Start Test
                </Button>
              }
              sx={{ mb: 2, alignItems: 'center' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Practice test due {format(parseISO(pendingTestTask.dueDate), 'MMM d')}
              </Typography>
            </Alert>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
              Weekly Progress
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {format(parseISO(currentWeek.startDate), 'MMM d')} – {format(parseISO(currentWeek.endDate), 'MMM d')}
            </Typography>
          </Box>

          {questionsThisWeek > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1 }}>
              {questionsThisWeek} question{questionsThisWeek !== 1 ? 's' : ''} attempted this week
            </Typography>
          )}

          {/* Days active strip */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, px: 0.5 }}>
            {weekDays.map((day, i) => {
              const dayStr = formatISO(day, { representation: 'date' });
              const isActive = activeDates.has(dayStr);
              return (
                <Tooltip key={dayStr} title={format(day, 'EEEE, MMM d')}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                      {DAY_LABELS[i]}
                    </Typography>
                    <Box sx={{
                      width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 }, borderRadius: '50%',
                      bgcolor: isActive ? theme.palette.primary.main : theme.palette.custom.lightGrey,
                      border: isActive ? 'none' : `1px solid ${theme.palette.divider}`,
                    }} />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Box sx={{ my: 1.5 }}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Skills Practice Questions</Typography>
              <Typography variant="body2" color="text.secondary">{skillsGoal.current} / {skillsGoal.target}</Typography>
              {skillsProgress >= 100 && <CheckCircle color="success" fontSize="small" sx={{ ml: 1 }} />}
            </Box>
            <LinearProgress variant="determinate" value={Math.min(skillsProgress, 100)}
              sx={{ height: 8, borderRadius: 1, backgroundColor: theme => theme.palette.custom.lightGrey,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme => skillsProgress >= 100 ? theme.palette.custom.green : theme.palette.primary.main
                }
              }} />
          </Box>

          <Box sx={{ my: 1.5 }}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Missed Questions Reviewed</Typography>
              <Typography variant="body2" color="text.secondary">{missedGoal.current} / {missedGoal.target}</Typography>
              {missedProgress >= 100 && <CheckCircle color="success" fontSize="small" sx={{ ml: 1 }} />}
            </Box>
            <LinearProgress variant="determinate" value={Math.min(missedProgress, 100)}
              sx={{ height: 8, borderRadius: 1, backgroundColor: theme => theme.palette.custom.lightGrey,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme => missedProgress >= 100 ? theme.palette.custom.green : theme.palette.primary.main
                }
              }} />
          </Box>

          <Button variant="outlined" onClick={onViewPlanClick} startIcon={<EditCalendar />} fullWidth sx={{ mt: 1, mb: 1 }}>
            View Full Plan
          </Button>

          {upcomingTasks.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
                Upcoming Tasks & Challenges
              </Typography>
              <Box sx={{ maxHeight: { xs: 180, sm: 150 }, overflow: 'auto', mb: 0 }}>
                <List dense disablePadding>
                  {upcomingTasks.map((task) => {
                    const hasRoute = !!getTaskRoute(task);
                    return (
                      <ListItem key={task.id} disablePadding
                        sx={{
                          ...(celebratingTaskId === task.id && { animation: `${celebrateKeyframes} 1.5s ease-out` }),
                          borderRadius: '4px',
                        }}>
                        <ListItemButton
                          onClick={() => hasRoute ? handleTaskClick(task.id) : handleCompleteTask(task.id)}
                          disabled={celebratingTaskId === task.id}
                          sx={{ py: 0.5, pr: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox edge="start" checked={false} disabled={celebratingTaskId === task.id}
                              onClick={(e) => handleCompleteTask(task.id, e)} />
                          </ListItemIcon>
                          <ListItemText
                            primary={task.title}
                            secondary={
                              <>
                                {task.description && (
                                  <Typography variant="body2" color="text.secondary"
                                    sx={{ whiteSpace: 'pre-line', display: { xs: 'none', sm: 'block' } }}>
                                    {task.description}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="warning.main">
                                  Due: {format(parseISO(task.dueDate!), 'MMM d')}
                                  {hasRoute ? ' · Tap to start' : ''}
                                </Typography>
                              </>
                            }
                            primaryTypographyProps={{ variant: 'body2', noWrap: false,
                              sx: { overflow: 'hidden', textOverflow: 'ellipsis',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </>
          )}

          {completedTasks.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
                Completed This Week
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: { xs: 120, sm: 150 }, overflow: 'auto' }}>
                <List dense disablePadding>
                  {completedTasks.map((task) => (
                    <ListItem key={task.id} disablePadding sx={{ opacity: 0.8 }}>
                      <ListItemButton disabled>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={task.title}
                          secondary={task.completionDate ? `Completed: ${format(parseISO(task.completionDate), 'MMM d')}` : null}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </Box>
    );

    return (
      <Box>
        <CardHeader
          title={`Your Study Plan - Week ${currentWeekNumber}`}
          titleTypographyProps={{
            sx: { fontSize: { xs: theme.typography.h6.fontSize, sm: theme.typography.h5.fontSize }, pb: 0 }
          }}
          subheader={paceMessage || (
            totalWeeks - currentWeekNumber <= 0 ? 'Less than 1 week left!'
              : totalWeeks - currentWeekNumber === 1 ? '1 week left!'
                : `${totalWeeks - currentWeekNumber} weeks until your test`
          )}
          subheaderTypographyProps={{ variant: 'body2', sx: { fontSize: theme.typography.body2.fontSize } }}
        />

        <Tabs value={widgetTab} onChange={(_, v) => setWidgetTab(v)} variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40,
            '& .MuiTab-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: 40, py: 1 } }}>
          <Tab label="This Week" value="thisWeek" />
          <Tab label="Progress" value="progress" />
        </Tabs>

        {widgetTab === 'thisWeek' ? renderThisWeekTab() : (
          <WeeklyProgressChart studyPlan={user.studyPlan} />
        )}
      </Box>
    );
  };

  const renderCreatePrompt = () => (
    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 2, px: { xs: 2, sm: 3 } }}>
      <Typography variant={isMobile ? "h5" : "h6"} align="center" gutterBottom>Ready to Prep?</Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
        Let's build your personalized study schedule based on your goals and timeline.
      </Typography>
      <Button variant="contained" onClick={onCreatePlanClick} startIcon={<EditCalendar />} size="small">
        Create Study Plan
      </Button>
    </CardContent>
  );

  const content = studyPlanExists ? renderPlanSummary() : renderCreatePrompt();

  if (embedded) return content;

  return (
    <Card elevation={1}>
      {content}
    </Card>
  );
};

export default StudyPlanWidget;
